import { useState, useEffect, useCallback } from 'react'
import { Plus, Calendar, Clock, User, Users, BookOpen, LogOut, Star, ThumbsUp, ThumbsDown, Minus, RefreshCw, BarChart3, Zap } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import {
  supabase,
  getCurrentUser,
  signOut,
  getBetaUserByEmail,
  createTrainingSession,
  getTrainingSessions,
  updateBetaUser,
  type BetaUser,
  type TrainingSession
} from '../../lib/supabase'
import { sanitizeText, validateUserInput, checkRateLimit, logSecurityEvent } from '../../utils/security'
import { useLanguage } from '../../contexts/LanguageContext'
import { hasAdminAccess, hasTrainerFeedbackAccess } from '../../lib/roles'
import { getApiBaseUrl } from '../../lib/apiBaseUrl'
import { matchCurrentScheduleSlot, getSlotsForDay, sessionTitleFromSlot, WEEKDAY_LABELS, type ScheduleSlot } from '../../lib/tkbcSchedule'
import TrainingFeedbackInput from '../training/TrainingFeedbackInput'
import TrainerFeedbackInput from '../training/TrainerFeedbackInput'

// Remove duplicate interfaces since they're imported from supabase.ts

export default function SimpleTracking() {
  const { t, language, setLanguage } = useLanguage()
  // Auth states
  const [authUser, setAuthUser] = useState<any>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)

  // App states
  const [user, setUser] = useState<BetaUser | null>(null)
  const [sessions, setSessions] = useState<TrainingSession[]>([])
  const [, setLoading] = useState(false)

  // Form states
  const [duration, setDuration] = useState<number>(60)
  const [content, setContent] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]) // Today as default
  const [rating, setRating] = useState<number>(3) // 1-5 stars, default 3
  const [goalAchievement, setGoalAchievement] = useState<'helped' | 'neutral' | 'not_helped' | null>(null)
  const [showTemplates, setShowTemplates] = useState(false)
  const [trainerToastMessage, setTrainerToastMessage] = useState<string | null>(null)
  const [trainerPrefillTemplate, setTrainerPrefillTemplate] = useState<'none' | 'technique' | 'conditioning'>('none')
  const [trainerQuickActionKey, setTrainerQuickActionKey] = useState(0)
  const [trainerSelectedSessionId, setTrainerSelectedSessionId] = useState<string>('')
  const [participantHandoffMode, setParticipantHandoffMode] = useState(false)
  const [participantHandoffKey, setParticipantHandoffKey] = useState(0)
  const [trainerAnalyticsLoading, setTrainerAnalyticsLoading] = useState(false)
  const [trainerAnalytics, setTrainerAnalytics] = useState<{
    received: number
    accepted: number
    rejected: number
    failed: number
    acceptance_rate_percent: number
    processed: number
  } | null>(null)
  // Live session creation (schedule-matched)
  const [showLiveSessionConfirm, setShowLiveSessionConfirm] = useState(false)
  const [liveSessionTitle, setLiveSessionTitle] = useState('')
  const [liveSessionDate, setLiveSessionDate] = useState('')
  const [liveSessionDuration, setLiveSessionDuration] = useState(60)
  const [liveSessionSlot, setLiveSessionSlot] = useState<ScheduleSlot | null>(null)
  const [liveSessionCreating, setLiveSessionCreating] = useState(false)

  const [userFeedbackAnalyticsLoading, setUserFeedbackAnalyticsLoading] = useState(false)
  const [userFeedbackAnalytics, setUserFeedbackAnalytics] = useState<{
    received: number
    accepted: number
    rejected: number
    failed: number
    acceptance_rate_percent: number
    average_satisfaction: number
    average_completion_percent: number
    would_repeat_percent: number
    processed: number
  } | null>(null)


  // Training templates based on TKBC 60min Standard
  const trainingTemplates = {
    de: [
      {
        title: 'TKBC Standard Session',
        duration: 60,
        content: 'Aufwärmen 12min (Seilspringen 3min + Aktivierung 6min + Shadow Boxing 3min) → Cardio Prep 8min (Seilspringen 2x3min/1min Pause) → Stretching 5min → Haupttraining 28min (Heavy Bag 5x4min/1min oder Partner Pads) → Cool-down 7min'
      },
      {
        title: 'Heavy Bag Variante',
        duration: 60,
        content: 'Aufwärmen 12min → Cardio Prep 8min → Stretching 5min → Heavy Bag Training 28min: Runde 1 (4min Basis-Kombinationen 70%), Runde 2 (4min Power-Strikes 85%), Runde 3 (4min Footwork 75%), Runde 4 (4min Clinch-Work 80%), Runde 5 (4min Conditioning 90%) → Cool-down 7min'
      },
      {
        title: 'Partner Pad Work',
        duration: 60,
        content: 'Aufwärmen 12min → Cardio Prep 8min → Stretching 5min → Partner Pads 28min: 4x5min (Kombinationen, Reaktion, Konter, Defensive) mit 1min Pausen und Partnerwechsel alle 2 Runden → Cool-down 7min'
      },
      {
        title: 'Minimales Equipment',
        duration: 60,
        content: 'Aufwärmen 12min → Cardio Prep 8min → Stretching 5min → Shadow Boxing & Conditioning 28min: Shadow Boxing 15min (Technik 5min + Kombinationen 5min + Power 5min), Conditioning 10min (Muay Thai spezifisches Körpergewicht + Core) → Cool-down 7min'
      },
      {
        title: 'Anfänger Anpassung',
        duration: 60,
        content: 'Aufwärmen 15min (verlängert) → Cardio Prep 6min (reduziert) → Stretching 8min (erweitert) → Haupttraining 24min (Intensität -20%, Pausen +30sec) → Cool-down 7min + extra Stretching'
      }
    ],
    en: [
      {
        title: 'Basic Technique Session',
        duration: 60,
        content: 'Warm-up incl. stretching 15min → Jab-Cross combination 3x3min → Jab-Cross-Hook 3x3min → Teep kicks 3x2min → Roundhouse kicks 3x2min → Cool-down 5min'
      },
      {
        title: 'Combination Training',
        duration: 90,
        content: 'Warm-up 20min → Jab-Cross-Low-Kick 4x3min → Jab-Cross-Low-Kick-Switch-Kick 4x3min → Jab-Cross-Hook-Low-Kick 3x3min → Teep-Hook-Cross 3x2min → Stretching 10min'
      },
      {
        title: 'Heavy Bag Power',
        duration: 45,
        content: 'Warm-up 10min → Power jabs on heavy bag 3x2min → Cross-Hook combinations 4x2min → Alternating low kicks 3x2min → Knee strikes 2x2min → Cool-down 5min'
      },
      {
        title: 'Pad-Work Session',
        duration: 75,
        content: 'Warm-up 15min → Basic combinations on pads 5x3min → Reaction drills 4x2min → Kick-Punch combinations 4x3min → Clinch work 3x2min → Stretching 10min'
      },
      {
        title: 'Conditioning Training',
        duration: 60,
        content: 'Warm-up 10min → Burpees 3x1min → Shadow boxing 4x2min → Mountain climbers 3x1min → Heavy bag intervals 5x1min → Planks 3x1min → Cool-down 10min'
      }
    ]
  }

  const loadUserData = useCallback(async (userEmail: string, userId: string | null) => {
    setLoading(true)
    try {

      // Load user profile by email instead of ID
      const { data: userData, error: userError } = await getBetaUserByEmail(userEmail)

      if (userError) {
        console.error('Error loading beta user by email:', userError)
        setUser(null)
      } else {
        setUser(userData ?? null)
      }

      const effectiveUserId = userId || authUser?.id || null
      if (effectiveUserId) {
        console.log('Loading training sessions for user:', effectiveUserId)
        const { data: sessionsData, error: sessionsError } = await getTrainingSessions(effectiveUserId)
        if (sessionsError) {
          console.error('Error loading training sessions:', sessionsError)
        } else if (sessionsData) {
          console.log('Loaded training sessions:', sessionsData)
          setSessions(sessionsData)
        } else {
          console.log('No training sessions found for user')
          setSessions([])
        }
      } else {
        console.warn('Skipping training session load: missing userId')
        setSessions([])
      }
    } catch (error) {
      console.error('Load user data error:', error)
    } finally {
      setLoading(false)
    }
  }, [authUser?.id])

  const checkAuth = useCallback(async () => {
    try {
      const { data: { user } } = await getCurrentUser()
      if (user) {
        setAuthUser(user)
        setIsAuthenticated(true)
        await loadUserData(user.email || '', user.id)
      } else {
        setAuthUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error('Auth check error:', error)
      setAuthUser(null)
      setIsAuthenticated(false)
    }
  }, [loadUserData])

  // Check authentication
  useEffect(() => {
    const initAuth = async () => {
      await checkAuth()
      setAuthLoading(false) // Ensure loading stops after initial check
    }
    initAuth()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_, session) => {
      if (session) {
        setAuthUser(session.user)
        setIsAuthenticated(true)
        await loadUserData(session.user.email || '', session.user.id)
      } else {
        setAuthUser(null)
        setIsAuthenticated(false)
        setUser(null)
        setSessions([])
      }
      setAuthLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [checkAuth, loadUserData])

  const loadTrainerFeedbackAnalytics = useCallback(async () => {
    const canUseTrainerFeedback = hasTrainerFeedbackAccess(authUser)
    if (!isAuthenticated || !authUser || !canUseTrainerFeedback) {
      return
    }

    setTrainerAnalyticsLoading(true)
    try {
      const session = (await supabase.auth.getSession()).data.session
      const token = session?.access_token
      if (!token) {
        return
      }

      const response = await fetch(`${getApiBaseUrl()}/b2b/trainer-feedback/analytics`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch trainer analytics')
      }

      const data = await response.json()
      setTrainerAnalytics(data.analytics || null)
    } catch (error) {
      console.error('Failed to load trainer feedback analytics:', error)
    } finally {
      setTrainerAnalyticsLoading(false)
    }
  }, [authUser, isAuthenticated])

  const loadUserFeedbackAnalytics = useCallback(async () => {
    const canUseTrainerFeedback = hasTrainerFeedbackAccess(authUser)
    if (!isAuthenticated || !authUser || !canUseTrainerFeedback) {
      return
    }

    setUserFeedbackAnalyticsLoading(true)
    try {
      const session = (await supabase.auth.getSession()).data.session
      const token = session?.access_token
      if (!token) {
        return
      }

      const response = await fetch(`${getApiBaseUrl()}/training/feedback/analytics`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch user feedback analytics')
      }

      const data = await response.json()
      setUserFeedbackAnalytics(data.analytics || null)
    } catch (error) {
      console.error('Failed to load user feedback analytics:', error)
    } finally {
      setUserFeedbackAnalyticsLoading(false)
    }
  }, [authUser, isAuthenticated])

  useEffect(() => {
    void loadTrainerFeedbackAnalytics()
    void loadUserFeedbackAnalytics()
  }, [loadTrainerFeedbackAnalytics, loadUserFeedbackAnalytics])

  const handleCreateLiveSession = useCallback(async () => {
    if (!authUser?.id || !liveSessionTitle.trim()) return
    setLiveSessionCreating(true)
    try {
      const contentParts = [liveSessionTitle]
      if (liveSessionSlot) {
        contentParts.push(`[${liveSessionSlot.type}]`)
      }

      const { data, error } = await createTrainingSession({
        user_id: authUser.id,
        date: liveSessionDate || new Date().toISOString().split('T')[0],
        duration: liveSessionDuration,
        content: contentParts.join(' '),
        notes: liveSessionSlot
          ? `Auto: TKBC Stundenplan ${liveSessionSlot.startTime}–${liveSessionSlot.endTime}`
          : 'Manuell erstellt via Jetzt-Session',
        rating: 3,
        goal_achievement: 'neutral'
      })

      if (error) throw error

      if (data) {
        setSessions((prev) => [data, ...prev])
        setTrainerSelectedSessionId(data.id)
      }

      setShowLiveSessionConfirm(false)
      setTrainerPrefillTemplate('none')
      setTrainerQuickActionKey((prev) => prev + 1)
    } catch (err) {
      console.error('Failed to create live session:', err)
    } finally {
      setLiveSessionCreating(false)
    }
  }, [authUser?.id, liveSessionTitle, liveSessionDate, liveSessionDuration, liveSessionSlot])

  // Listen for user approval events (when admin approves user)
  useEffect(() => {
    const handleUserApproved = (event: CustomEvent) => {
      // Skip auto-refresh for admin users (they approve themselves)
      const isAdminUser = authUser?.email === 'sh@haugk.com'

      if (authUser && event.detail.email === authUser.email && !isAdminUser) {
        // User approval event received, refreshing user data
        // Current user was approved, refresh their data
        loadUserData(authUser.email || '', authUser.id)
      } else if (isAdminUser) {
        // Admin user - skipping auto-refresh on approval
      }
    }

    window.addEventListener('userApproved', handleUserApproved as EventListener)
    return () => window.removeEventListener('userApproved', handleUserApproved as EventListener)
  }, [authUser, loadUserData])

  const saveSession = async () => {
    if (!user || !user.profile_completed || !content || duration <= 0 || !authUser) return

    // Security: Rate limiting
    if (!checkRateLimit(`training_save_${authUser.id}`, 5, 60000)) {
      alert('Zu viele Anfragen. Bitte warten Sie eine Minute.')
      logSecurityEvent('Rate limit exceeded for training save', { userId: authUser.id })
      return
    }

    // Security: Input validation
    const contentValidation = validateUserInput.trainingContent(content)
    if (!contentValidation.isValid) {
      alert(contentValidation.error)
      logSecurityEvent('Invalid training content', { content: content.substring(0, 100) })
      return
    }

    const notesValidation = validateUserInput.notes(notes)
    if (!notesValidation.isValid) {
      alert(notesValidation.error)
      logSecurityEvent('Invalid training notes', { notes: notes.substring(0, 100) })
      return
    }

    const durationValidation = validateUserInput.duration(duration)
    if (!durationValidation.isValid) {
      alert(durationValidation.error)
      return
    }

    setLoading(true)
    try {
      // Security: Sanitize all input data
      const sessionData = {
        user_id: authUser.id,
        date: date,
        duration,
        content: sanitizeText(content),
        notes: sanitizeText(notes || ''),
        rating: rating, // User's rating from UI (1-5 stars)
        goal_achievement: goalAchievement // User's goal feedback
      }

      console.log('Attempting to save training session:', sessionData)
      const { data: newSession, error } = await createTrainingSession(sessionData)

      if (error) {
        console.error('Save session error:', error)
        console.error('Full error details:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint
        })
        alert(`Fehler beim Speichern des Trainings: ${error.message}`)
        logSecurityEvent('Training save error', { error: error.message })
        return
      }

      if (newSession) {
        console.log('Training session saved successfully:', newSession)
        setSessions(prev => [newSession, ...prev])

        // Reset form
        setDuration(60)
        setContent('')
        setNotes('')
        setDate(new Date().toISOString().split('T')[0]) // Reset to today
        setRating(3) // Reset to default 3 stars
        setGoalAchievement(null) // Reset goal achievement feedback

        alert(`Training erfolgreich gespeichert! (${duration} Minuten)`)
        logSecurityEvent('Training saved successfully', { userId: authUser.id })
      } else {
        console.warn('No training session data returned from createTrainingSession')
        alert('Training wurde möglicherweise nicht gespeichert - bitte prüfen Sie Ihre Trainingsliste')
      }
    } catch (error) {
      console.error('Save session error:', error)
      alert('Fehler beim Speichern des Trainings')
      logSecurityEvent('Training save exception', { error })
    } finally {
      setLoading(false)
    }
  }

  const applyTemplate = (template: any) => {
    setContent(template.content)
    setDuration(template.duration)
    setShowTemplates(false)
  }


  const changeLanguage = (newLang: 'de' | 'en') => {
    setLanguage(newLang)
  }

  const handleLogout = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  // Show loading while checking auth - smaller inline loading for better UX
  if (authLoading) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-600 dark:text-slate-300 text-sm">{t('common.loading')}</p>
        </div>
      </div>
    )
  }

  // Show registration prompt if not authenticated (for Beta page)
  if (!isAuthenticated) {
    return (
      <div className="max-w-md mx-auto">
        <Card className="p-6 text-center">
          <div className="w-16 h-16 bg-purple-100 dark:bg-ocean-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-purple-600 dark:text-ocean-400" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-2">
            {language === 'de' ? 'Beta-Zugang' : 'Beta Access'}
          </h3>
          <p className="text-gray-600 dark:text-slate-300 mb-4">
            {language === 'de'
              ? 'Registriere dich für exklusiven Beta-Zugang zum TrainIQ System'
              : 'Register for exclusive beta access to the TrainIQ system'
            }
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => window.location.href = '/register'}
              className="w-full bg-purple-500 hover:bg-purple-600 dark:bg-ocean-500 dark:hover:bg-ocean-400 text-white"
            >
              {language === 'de' ? 'Beta-Registrierung' : 'Beta Registration'}
            </Button>
            <p className="text-sm text-gray-500 dark:text-slate-400">
              {language === 'de' ? 'Bereits registriert?' : 'Already registered?'}{' '}
              <a href="/login" className="text-purple-600 hover:text-purple-500 dark:text-ocean-400 dark:hover:text-ocean-300 font-medium">
                {language === 'de' ? 'Hier anmelden' : 'Login here'}
              </a>
            </p>
          </div>
        </Card>
      </div>
    )
  }

  // Check if current user is admin (bypass normal flow)
  const isAdmin = hasAdminAccess(authUser)
  const canUseTrainerFeedback = hasTrainerFeedbackAccess(authUser)

  // Debug render logic
  // Render decision based on user state and approval status

  // Special case: Admin user should always see training interface if user exists
  if (isAdmin && user) {
    // Admin bypass: Showing training interface
    // Admin sees training interface regardless of approval status
  }

  const profileIncomplete = user?.profile_completed === false
  const trainerSessionOptions = sessions.map((session) => ({
    id: session.id,
    label: `${new Date(session.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')} - ${session.duration} min - ${sanitizeText(session.content || '').slice(0, 40)}`
  }))
  const trainerClientOptions = Array.from(
    new Map(
      sessions.map((session) => {
        const displayName = user?.id === session.user_id
          ? (user?.name || authUser?.email || 'Client')
          : `Client ${session.user_id.slice(0, 8)}`

        return [session.user_id, {
          id: session.user_id,
          label: displayName,
          name: displayName
        }]
      })
    ).values()
  )
  const selectedTrainerSession =
    sessions.find((session) => session.id === trainerSelectedSessionId) || sessions[0] || null

  // Show pending approval message if user exists but not approved (and not admin)
  if (user && !user.approved && !isAdmin) {
    // Rendering: Pending Approval Message
    return (
      <div className="max-w-4xl mx-auto p-4">
        <Card className="p-4 text-center">
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">
              {language === 'de' ? 'Warten auf Freigabe' : 'Waiting for Approval'}
            </h3>
            <p className="text-gray-600 dark:text-slate-200 max-w-md mx-auto">
              {language === 'de'
                ? 'Dein Profil wird aktuell geprüft. Du erhältst eine E-Mail, sobald du mit dem Training beginnen kannst.'
                : 'Your profile is currently being reviewed. You will receive an email once you can start training.'
              }
            </p>
            <div className="text-sm text-gray-500 dark:text-slate-300 space-y-1 bg-gray-50 dark:bg-night-700 rounded-lg p-4">
              <p><strong>{language === 'de' ? 'Name:' : 'Name:'}</strong> {sanitizeText(user.name || '')}</p>
              <p><strong>{language === 'de' ? 'Ziele:' : 'Goals:'}</strong> {
                Array.isArray(user.training_goal)
                  ? user.training_goal.map(goal => sanitizeText(goal || '')).join(', ')
                  : sanitizeText((user.training_goal as string | null | undefined) || '')
              }</p>
              <p><strong>{language === 'de' ? 'Erfahrung:' : 'Experience:'}</strong> {sanitizeText(user.experience_level || '')}</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
            >
              {language === 'de' ? 'Abmelden' : 'Logout'}
            </button>
          </div>
        </Card>
      </div>
    )
  }

  // Show waiting message if approval pending (no beta profile yet)
  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <Card className="p-4 text-center">
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
              <Clock className="w-8 h-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-slate-100">
              {language === 'de' ? 'Registrierung erhalten' : 'Registration received'}
            </h3>
            <p className="text-gray-600 dark:text-slate-200 max-w-md mx-auto">
              {language === 'de'
                ? 'Bitte warte, bis Simon deine Anmeldung freigeschaltet hat. Danach kannst du dein Beta-Profil vervollständigen.'
                : 'Please wait for Simon to approve your registration. Once approved, you can complete your beta profile.'}
            </p>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
            >
              {language === 'de' ? 'Abmelden' : 'Logout'}
            </button>
          </div>
        </Card>
      </div>
    )
  }

  if (profileIncomplete) {
    return <UserSetup
      language={language}
      onLanguageChange={changeLanguage}
      authUser={authUser}
      existingUser={user}
      onComplete={async (userData) => {
        try {
          if (!authUser?.id) {
            alert(language === 'de' ? 'Benutzer-ID fehlt. Bitte erneut anmelden.' : 'Missing user ID. Please log in again.')
            return
          }

          if (!checkRateLimit(`profile_complete_${authUser.id}`, 3, 300000)) {
            alert(language === 'de' ? 'Zu viele Versuche. Bitte warten.' : 'Too many attempts. Please wait.')
            logSecurityEvent('Rate limit exceeded for profile completion', { userId: authUser.id })
            return
          }

          const nameValidation = validateUserInput.name(userData.name)
          if (!nameValidation.isValid) {
            alert(nameValidation.error)
            logSecurityEvent('Invalid name in profile completion', { name: userData.name })
            return
          }

          const ageValidation = validateUserInput.age(userData.age)
          if (!ageValidation.isValid) {
            alert(ageValidation.error)
            return
          }

          const { data: updatedUser, error } = await updateBetaUser(authUser.id, {
            name: sanitizeText(userData.name),
            age: userData.age,
            experience_level: userData.experience_level,
            training_goal: userData.training_goal.map((goal: string) => sanitizeText(goal)),
            profile_completed: true,
            approved: user.approved ?? true,
            email: authUser.email || user.email
          })

          if (error) {
            console.error('Profile completion error:', error)
            alert(language === 'de'
              ? 'Fehler beim Speichern des Profils. Bitte versuche es erneut.'
              : 'Error saving profile. Please try again.')
            return
          }

          if (updatedUser) {
            setUser(updatedUser)
            alert(language === 'de'
              ? 'Profil gespeichert! Viel Erfolg beim Training.'
              : 'Profile saved! You are ready to start training.')
            await loadUserData(authUser.email || '', authUser.id)
          }
        } catch (error) {
          console.error('Profile completion error:', error)
          alert(language === 'de'
            ? 'Fehler beim Speichern des Profils. Bitte versuche es erneut.'
            : 'Error saving profile. Please try again.')
        }
      }}
    />
  }


  // Rendering: Training Interface (user approved)
  if (participantHandoffMode && canUseTrainerFeedback) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <Card className="p-4 border-blue-200 dark:border-ocean-500/40 bg-blue-50 dark:bg-ocean-500/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-blue-900 dark:text-ocean-100">
                {language === 'de' ? 'Teilnehmer-Modus aktiv' : 'Participant mode active'}
              </h3>
              <p className="text-sm text-blue-800 dark:text-ocean-200 mt-1">
                {language === 'de'
                  ? 'Der Teilnehmer kann jetzt direkt auf diesem Tablet Feedback eingeben.'
                  : 'The participant can now submit feedback directly on this tablet.'}
              </p>
              {selectedTrainerSession && (
                <p className="mt-2 text-xs text-blue-700 dark:text-ocean-200/90">
                  {language === 'de' ? 'Aktive Session:' : 'Active session:'}{' '}
                  {new Date(selectedTrainerSession.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')} - {selectedTrainerSession.duration} min
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              className="text-xs"
              onClick={() => setParticipantHandoffMode(false)}
            >
              {language === 'de' ? 'Zur Traineransicht' : 'Back to trainer view'}
            </Button>
          </div>
        </Card>

        <TrainingFeedbackInput
          key={participantHandoffKey}
          sessionId={selectedTrainerSession?.id || sessions[0]?.id || ''}
          userId={selectedTrainerSession?.user_id || user?.id || authUser?.id || ''}
          exercises={[]}
          showTriggerButton={false}
          defaultOpen
          inlineMode
          titleOverride={language === 'de' ? 'Teilnehmer-Feedback' : 'Participant Feedback'}
          subtitleOverride={language === 'de'
            ? 'Bitte gib direkt nach dem Training dein Feedback ein.'
            : 'Please enter your feedback directly after the training session.'}
          submitLabelOverride={language === 'de' ? 'Feedback absenden' : 'Submit feedback'}
          onOpenChange={(open) => {
            if (!open) {
              setParticipantHandoffMode(false)
            }
          }}
          onFeedbackSubmitted={() => {
            setParticipantHandoffMode(false)
            setTrainerToastMessage(
              language === 'de'
                ? 'Teilnehmer-Feedback wurde erfolgreich gespeichert.'
                : 'Participant feedback was saved successfully.'
            )
            setTimeout(() => setTrainerToastMessage(null), 3500)
            if (authUser?.email) {
              void loadUserData(authUser.email, authUser?.id || null)
            }
            void loadUserFeedbackAnalytics()
          }}
        />
      </div>
    )
  }

  // Rendering: Training Interface (user approved)
  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header with Language Toggle and Logout */}
      <div className="text-center">
        <div className="flex justify-between items-center mb-3">
          {/* User info */}
          <div className="text-left">
            <p className="text-sm text-gray-600 dark:text-slate-200">
              {authUser?.email}
            </p>
          </div>

          {/* Controls */}
          <div className="flex items-center space-x-2">
            {/* Language Toggle */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => changeLanguage('de')}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  language === 'de'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100'
                }`}
              >
                🇩🇪 DE
              </button>
              <button
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                  language === 'en'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-300 dark:hover:text-slate-100'
                }`}
              >
                🇺🇸 EN
              </button>
            </div>


            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors flex items-center space-x-1"
            >
              <LogOut className="w-3 h-3" />
              <span>Abmelden</span>
            </button>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-1">
          {t('training.title')}
        </h1>
        <p className="text-gray-600 dark:text-slate-200 text-sm">
          {t('training.hello')} {sanitizeText(user.name || '')}! {t('training.trackSimple')}
        </p>
      </div>

      {/* Quick Add Training */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">
            {t('training.addTraining')}
          </h2>
          <Button
            onClick={() => setShowTemplates(!showTemplates)}
            variant="outline"
            className="flex items-center space-x-1 text-sm px-2 py-1"
          >
            <BookOpen className="w-3 h-3" />
            <span>{t('training.templates')}</span>
          </Button>
        </div>

        {/* Training Templates */}
        {showTemplates && (
          <div className="mb-4 p-3 bg-gray-50 dark:bg-night-800 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">{t('training.templates')}</h3>
            <div className="space-y-2">
              {trainingTemplates[language].map((template, index) => (
                <button
                  key={index}
                  onClick={() => applyTemplate(template)}
                  className="w-full text-left p-2 bg-white dark:bg-night-700 border border-gray-200 dark:border-night-600 rounded-lg hover:border-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 dark:hover:border-green-600 transition-colors"
                >
                  <div className="font-medium text-gray-900 dark:text-slate-100 mb-1 text-sm">{template.title}</div>
                  <div className="text-xs text-gray-600 dark:text-slate-200 mb-1">{template.duration} min</div>
                  <div className="text-xs text-gray-500 dark:text-slate-300 leading-relaxed">{template.content}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              {t('training.whatTrained')}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('training.placeholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-lg bg-white dark:bg-night-700 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-transparent text-sm"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                {t('training.duration')}: {duration} {t('training.minutes')}
              </label>
              <input
                type="range"
                min="15"
                max="180"
                step="15"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 dark:bg-night-600 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 dark:text-slate-300 mt-1">
                <span>15min</span>
                <span>180min</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {t('training.date')}
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-lg bg-white dark:bg-night-700 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              {t('training.notes')}
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('training.notesPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-lg bg-white dark:bg-night-700 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-transparent text-sm"
            />
          </div>

          {/* Rating System */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
              {t('training.rating')}
            </label>
            <div className="flex items-center space-x-1">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className={`p-1 transition-colors ${
                    star <= rating
                      ? 'text-yellow-500 hover:text-yellow-400'
                      : 'text-gray-300 hover:text-yellow-300 dark:text-gray-600 dark:hover:text-yellow-400'
                  }`}
                >
                  <Star className="w-6 h-6 fill-current" />
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-600 dark:text-slate-300">
                ({rating}/5)
              </span>
            </div>
          </div>

          {/* Goal Achievement Feedback */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-2">
              {t('training.goalHelp')}
            </label>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setGoalAchievement('helped')}
                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  goalAchievement === 'helped'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-2 border-green-300 dark:border-green-600'
                    : 'bg-gray-100 dark:bg-night-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-night-600 hover:bg-green-50 dark:hover:bg-green-900/20'
                }`}
              >
                <ThumbsUp className="w-4 h-4 mr-1" />
                {t('training.goalHelpful')}
              </button>
              <button
                type="button"
                onClick={() => setGoalAchievement('neutral')}
                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  goalAchievement === 'neutral'
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-600'
                    : 'bg-gray-100 dark:bg-night-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-night-600 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                }`}
              >
                <Minus className="w-4 h-4 mr-1" />
                {t('training.goalNeutral')}
              </button>
              <button
                type="button"
                onClick={() => setGoalAchievement('not_helped')}
                className={`flex-1 flex items-center justify-center px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  goalAchievement === 'not_helped'
                    ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-2 border-red-300 dark:border-red-600'
                    : 'bg-gray-100 dark:bg-night-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-night-600 hover:bg-red-50 dark:hover:bg-red-900/20'
                }`}
              >
                <ThumbsDown className="w-4 h-4 mr-1" />
                {t('training.goalNotHelpful')}
              </button>
            </div>
          </div>

          <Button
            onClick={saveSession}
            disabled={!content || duration <= 0}
            className="w-full bg-green-500 hover:bg-green-600 text-white py-2 flex items-center justify-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{t('training.saveTraining')}</span>
          </Button>
        </div>
      </Card>

      {/* Recent Sessions */}
      {sessions.length > 0 && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3">
            {t('training.recentTrainings')}
          </h3>
          <div className="space-y-2">
            {sessions
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .slice(0, 5)
              .map(session => (
                <div key={session.id} className="border border-gray-200 dark:border-night-600 bg-white dark:bg-night-800 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-slate-100 text-sm">{sanitizeText(session.content)}</p>
                      {session.notes && (
                        <p className="text-xs text-gray-600 dark:text-slate-300 mt-1">"{sanitizeText(session.notes)}"</p>
                      )}
                      {/* Rating and Goal Achievement Display */}
                      <div className="flex items-center space-x-3 mt-2">
                        {/* Rating Stars */}
                        <div className="flex items-center space-x-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star
                              key={star}
                              className={`w-3 h-3 ${
                                star <= (session.rating || 3)
                                  ? 'text-yellow-500 fill-current'
                                  : 'text-gray-300 dark:text-gray-600'
                              }`}
                            />
                          ))}
                          <span className="text-xs text-gray-500 dark:text-slate-400">
                            ({session.rating || 3}/5)
                          </span>
                        </div>
                        {/* Goal Achievement Indicator */}
                        {session.goal_achievement && (
                          <div className="flex items-center space-x-1">
                            {session.goal_achievement === 'helped' && (
                              <div className="flex items-center space-x-1 text-green-600 dark:text-green-400">
                                <ThumbsUp className="w-3 h-3" />
                                <span className="text-xs">
                                  {language === 'de' ? 'Hilfreich' : 'Helpful'}
                                </span>
                              </div>
                            )}
                            {session.goal_achievement === 'neutral' && (
                              <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400">
                                <Minus className="w-3 h-3" />
                                <span className="text-xs">
                                  {language === 'de' ? 'Neutral' : 'Neutral'}
                                </span>
                              </div>
                            )}
                            {session.goal_achievement === 'not_helped' && (
                              <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                                <ThumbsDown className="w-3 h-3" />
                                <span className="text-xs">
                                  {language === 'de' ? 'Nicht hilfreich' : 'Not helpful'}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-xs text-gray-500 dark:text-slate-300 ml-3">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{session.duration} min</span>
                      </div>
                      <div className="flex items-center space-x-1 mt-1">
                        <Calendar className="w-3 h-3" />
                        <span>{new Date(session.date).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {sessions.length > 5 && (
            <div className="text-center mt-3 text-xs text-gray-500 dark:text-slate-300">
              {language === 'de'
                ? `... und ${sessions.length - 5} weitere Trainings`
                : `... and ${sessions.length - 5} more trainings`
              }
            </div>
          )}
        </Card>
      )}

      {/* Stats */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3">{t('training.statistics')}</h3>
        <div className="grid grid-cols-2 gap-3">
          <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-xl font-bold text-green-600 dark:text-green-400">{sessions.length}</div>
            <div className="text-xs text-green-700 dark:text-green-300">{t('training.totalTrainings')}</div>
          </div>
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-xl font-bold text-blue-600 dark:text-blue-400">
              {sessions.reduce((total, s) => total + s.duration, 0)}
            </div>
            <div className="text-xs text-blue-700 dark:text-blue-300">{t('training.minutesTrained')}</div>
          </div>
        </div>
      </Card>

      {canUseTrainerFeedback && (
        <Card className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-3">
            {language === 'de' ? 'Trainer-Tools' : 'Trainer Tools'}
          </h3>
          <div className="text-sm text-gray-600 dark:text-slate-300 space-y-2">
            <p>
              {language === 'de'
                ? 'Erfasse strukturiertes Trainer-Feedback je Session und verbessere damit die naechsten Empfehlungen.'
                : 'Capture structured trainer feedback per session to improve upcoming recommendations.'}
            </p>
            <p>
              {language === 'de'
                ? `Verfuegbare Sessions fuer Feedback: ${sessions.length}`
                : `Available sessions for feedback: ${sessions.length}`}
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Submits' : 'Submits'}:</span>{' '}
                {trainerAnalytics?.received ?? 0}
              </div>
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Akzeptiert' : 'Accepted'}:</span>{' '}
                {trainerAnalytics?.accepted ?? 0}
              </div>
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Fehler' : 'Failed'}:</span>{' '}
                {(trainerAnalytics?.rejected ?? 0) + (trainerAnalytics?.failed ?? 0)}
              </div>
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Acceptance' : 'Acceptance'}:</span>{' '}
                {trainerAnalytics?.acceptance_rate_percent ?? 0}%
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mt-2">
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Teilnehmer-Submits' : 'Participant submits'}:</span>{' '}
                {userFeedbackAnalytics?.received ?? 0}
              </div>
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Ø Zufriedenheit' : 'Avg satisfaction'}:</span>{' '}
                {userFeedbackAnalytics?.average_satisfaction ?? 0}/5
              </div>
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Ø Completion' : 'Avg completion'}:</span>{' '}
                {userFeedbackAnalytics?.average_completion_percent ?? 0}%
              </div>
              <div className="bg-gray-100 dark:bg-night-700 rounded px-2 py-1">
                <span className="font-semibold">{language === 'de' ? 'Would repeat' : 'Would repeat'}:</span>{' '}
                {userFeedbackAnalytics?.would_repeat_percent ?? 0}%
              </div>
            </div>
          </div>
          <div className="mt-2 flex justify-end">
            <Button
              variant="ghost"
              onClick={() => {
                void loadTrainerFeedbackAnalytics()
                void loadUserFeedbackAnalytics()
              }}
              className="text-xs"
              disabled={trainerAnalyticsLoading || userFeedbackAnalyticsLoading}
            >
              {trainerAnalyticsLoading || userFeedbackAnalyticsLoading ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <BarChart3 className="w-4 h-4 mr-1" />
              )}
              {language === 'de' ? 'Analytics aktualisieren' : 'Refresh analytics'}
            </Button>
          </div>
          {/* Live-Session starten (Schedule Match) */}
          {(() => {
            const now = new Date()
            const matched = matchCurrentScheduleSlot(now)
            const todaySlots = getSlotsForDay(now.getDay())
            const dayLabel = WEEKDAY_LABELS[now.getDay()]?.[language] || ''

            return (
              <div className="mt-3 space-y-2">
                <Button
                  variant="primary"
                  onClick={() => {
                    const slot = matched || todaySlots[0]
                    const title = slot
                      ? sessionTitleFromSlot(slot, language)
                      : (language === 'de' ? 'Freie Session' : 'Free Session')
                    const today = new Date().toISOString().split('T')[0]
                    setLiveSessionTitle(title)
                    setLiveSessionDate(today)
                    setLiveSessionDuration(slot?.durationMinutes ?? 60)
                    setLiveSessionSlot(slot ?? null)
                    setShowLiveSessionConfirm(true)
                  }}
                  className="w-full text-sm"
                >
                  <Zap className="w-4 h-4 mr-2" />
                  {matched
                    ? (language === 'de'
                        ? `Jetzt-Session: ${sessionTitleFromSlot(matched, 'de')}`
                        : `Start now: ${sessionTitleFromSlot(matched, 'en')}`)
                    : (language === 'de'
                        ? 'Jetzt-Session starten'
                        : 'Start live session')}
                </Button>

                {todaySlots.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-slate-400">
                    <span className="font-semibold">{dayLabel}:</span>{' '}
                    {todaySlots.map((s) => `${s.startTime} ${s.title[language]}`).join(' · ')}
                  </div>
                )}
                {todaySlots.length === 0 && (
                  <div className="text-xs text-gray-400 dark:text-slate-500">
                    {language === 'de'
                      ? `Kein Stundenplan fuer ${dayLabel} hinterlegt.`
                      : `No schedule for ${dayLabel}.`}
                  </div>
                )}
              </div>
            )
          })()}

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                if (sessions[0]?.id) {
                  setTrainerSelectedSessionId(sessions[0].id)
                }
                setTrainerPrefillTemplate('none')
                setTrainerQuickActionKey((prev) => prev + 1)
              }}
              className="text-xs"
            >
              {language === 'de' ? 'Letzte Session bewerten' : 'Rate latest session'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setTrainerPrefillTemplate('technique')
                setTrainerQuickActionKey((prev) => prev + 1)
              }}
              className="text-xs"
            >
              {language === 'de' ? 'Technik-Template' : 'Technique template'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setTrainerPrefillTemplate('conditioning')
                setTrainerQuickActionKey((prev) => prev + 1)
              }}
              className="text-xs"
            >
              {language === 'de' ? 'Kondition-Template' : 'Conditioning template'}
            </Button>
          </div>
          <div className="mt-3">
            <label className="block text-xs font-semibold text-gray-600 dark:text-slate-300 mb-1">
              {language === 'de' ? 'Session fuer Teilnehmer-Feedback' : 'Session for participant feedback'}
            </label>
            <select
              value={trainerSelectedSessionId || sessions[0]?.id || ''}
              onChange={(e) => setTrainerSelectedSessionId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
            >
              {trainerSessionOptions.map(option => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-3">
            <Button
              variant="primary"
              onClick={() => {
                if (sessions[0]?.id && !trainerSelectedSessionId) {
                  setTrainerSelectedSessionId(sessions[0].id)
                }
                setParticipantHandoffMode(true)
                setParticipantHandoffKey((prev) => prev + 1)
              }}
              className="w-full text-sm"
            >
              <Users className="w-4 h-4 mr-2" />
              {language === 'de' ? 'Tablet an Teilnehmer uebergeben' : 'Hand tablet to participant'}
            </Button>
          </div>
        </Card>
      )}

      {trainerToastMessage && (
        <div className="fixed top-6 right-6 z-50">
          <div className="bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm">
            {trainerToastMessage}
          </div>
        </div>
      )}

      {/* Live Session Confirmation Dialog */}
      {showLiveSessionConfirm && canUseTrainerFeedback && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <div className="p-6 space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">
                <Zap className="w-5 h-5 inline mr-2 text-yellow-500" />
                {language === 'de' ? 'Jetzt-Session anlegen' : 'Create live session'}
              </h3>

              {liveSessionSlot && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
                  <span className="font-semibold text-blue-800 dark:text-blue-200">
                    {language === 'de' ? 'Stundenplan-Match' : 'Schedule match'}:
                  </span>{' '}
                  <span className="text-blue-700 dark:text-blue-300">
                    {sessionTitleFromSlot(liveSessionSlot, language)}
                  </span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {language === 'de' ? 'Session-Titel' : 'Session title'}
                </label>
                <input
                  type="text"
                  value={liveSessionTitle}
                  onChange={(e) => setLiveSessionTitle(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    {language === 'de' ? 'Datum' : 'Date'}
                  </label>
                  <input
                    type="date"
                    value={liveSessionDate}
                    onChange={(e) => setLiveSessionDate(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    {language === 'de' ? 'Dauer (Min.)' : 'Duration (min)'}
                  </label>
                  <input
                    type="number"
                    min="15"
                    max="180"
                    value={liveSessionDuration}
                    onChange={(e) => setLiveSessionDuration(parseInt(e.target.value) || 60)}
                    className="w-full px-3 py-2 border rounded-lg text-sm dark:bg-gray-800 dark:border-gray-600"
                  />
                </div>
              </div>

              {!liveSessionSlot && (
                <div className="text-xs text-gray-400 dark:text-slate-500">
                  {language === 'de'
                    ? 'Kein passender Stundenplan-Eintrag gefunden. Session wird als freie Session erstellt.'
                    : 'No matching schedule entry found. Session will be created as a free session.'}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowLiveSessionConfirm(false)}
                  disabled={liveSessionCreating}
                  className="text-sm"
                >
                  {language === 'de' ? 'Abbrechen' : 'Cancel'}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => void handleCreateLiveSession()}
                  disabled={liveSessionCreating || !liveSessionTitle.trim()}
                  className="text-sm"
                >
                  {liveSessionCreating ? (
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-1" />
                  )}
                  {language === 'de' ? 'Session anlegen & Feedback starten' : 'Create session & start feedback'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Training Feedback Input */}
      {isAuthenticated && user && sessions.length > 0 && !canUseTrainerFeedback && (
        <TrainingFeedbackInput
          sessionId={sessions[0]?.id || ''}
          userId={user.id || authUser?.id || ''}
          exercises={[]} // Can be populated from session data if available
          onFeedbackSubmitted={() => {
            // Refresh sessions or show success message
            if (authUser?.email) {
              void loadUserData(authUser.email, authUser?.id || null)
            }
            void loadUserFeedbackAnalytics()
          }}
        />
      )}

      {/* Trainer Feedback Input (role-gated) */}
      {isAuthenticated && user && sessions.length > 0 && canUseTrainerFeedback && (
        <TrainerFeedbackInput
          sessionId={trainerSelectedSessionId || sessions[0]?.id || ''}
          trainerId={authUser?.id || ''}
          clientId={user.id || authUser?.id || ''}
          clientName={user.name || authUser?.email || 'Client'}
          sessionOptions={trainerSessionOptions}
          clientOptions={trainerClientOptions}
          prefillTemplate={trainerPrefillTemplate}
          quickActionKey={trainerQuickActionKey}
          onFeedbackSubmitted={(result) => {
            const sessionLabel = trainerSessionOptions.find(option => option.id === result.sessionId)?.label || result.sessionId
            const feedbackTarget = result.clientName || result.clientId
            setTrainerToastMessage(
              language === 'de'
                ? `Trainer-Feedback gespeichert fuer ${feedbackTarget} (${sessionLabel})`
                : `Trainer feedback saved for ${feedbackTarget} (${sessionLabel})`
            )
            setTimeout(() => setTrainerToastMessage(null), 3500)

            if (authUser?.email) {
              void loadUserData(authUser.email, authUser?.id || null)
            }
            void loadTrainerFeedbackAnalytics()
            void loadUserFeedbackAnalytics()
          }}
        />
      )}
    </div>
  )
}

function UserSetup({
  language,
  onLanguageChange,
  authUser,
  existingUser,
  onComplete
}: {
  language: 'de' | 'en'
  onLanguageChange: (lang: 'de' | 'en') => void
  authUser: any
  existingUser?: BetaUser | null
  onComplete: (user: any) => void
}) {
  const [formData, setFormData] = useState({
    name: sanitizeText(existingUser?.name || ''),
    age: existingUser?.age ? Number(existingUser.age) : 25,
    experience_level: (existingUser?.experience_level as 'anfaenger' | 'fortgeschritten' | 'erfahren' | '') || '',
    training_goal: Array.isArray(existingUser?.training_goal)
      ? (existingUser?.training_goal as string[])
      : (existingUser?.training_goal ? [existingUser.training_goal as string] : [])
  })

  useEffect(() => {
    setFormData({
      name: sanitizeText(existingUser?.name || ''),
      age: existingUser?.age ? Number(existingUser.age) : 25,
      experience_level: (existingUser?.experience_level as 'anfaenger' | 'fortgeschritten' | 'erfahren' | '') || '',
      training_goal: Array.isArray(existingUser?.training_goal)
        ? (existingUser?.training_goal as string[])
        : (existingUser?.training_goal ? [existingUser.training_goal as string] : [])
    })
  }, [existingUser])

  // Setup translations
  const setupT = {
    de: {
      title: 'TrainIQ Beta Setup',
      subtitle: 'Schnell einrichten, dann sofort loslegen!',
      name: 'Name',
      namePlaceholder: 'Dein Name',
      age: 'Alter',
      years: 'Jahre',
      experience: 'Muay Thai Erfahrung',
      goal: 'Hauptziel',
      start: 'Los geht\'s!',
      levels: [
        { key: 'anfaenger', label: 'Anfänger' },
        { key: 'fortgeschritten', label: 'Fortgeschritten' },
        { key: 'erfahren', label: 'Erfahren' }
      ],
      goals: [
        { key: 'fitness', label: 'Fitness & Kondition' },
        { key: 'technik', label: 'Technik verbessern' },
        { key: 'selbstverteidigung', label: 'Selbstverteidigung' },
        { key: 'wettkampf', label: 'Wettkampf-Vorbereitung' },
        { key: 'kraft', label: 'Kraft & Power' },
        { key: 'beweglichkeit', label: 'Beweglichkeit & Flexibilität' },
        { key: 'ausdauer', label: 'Ausdauer & Cardio' },
        { key: 'koordination', label: 'Koordination & Balance' },
        { key: 'sparring', label: 'Sparring & Kampfanwendung' },
        { key: 'rehabilitation', label: 'Rehabilitation & Therapy' },
        { key: 'gewichtsverlust', label: 'Gewichtsverlust' },
        { key: 'stressabbau', label: 'Stressabbau & Entspannung' },
        { key: 'spass', label: 'Spaß & Hobby' }
      ]
    },
    en: {
      title: 'TrainIQ Beta Setup',
      subtitle: 'Quick setup, then start immediately!',
      name: 'Name',
      namePlaceholder: 'Your Name',
      age: 'Age',
      years: 'years',
      experience: 'Muay Thai Experience',
      goal: 'Main Goal',
      start: 'Let\'s go!',
      levels: [
        { key: 'beginner', label: 'Beginner' },
        { key: 'intermediate', label: 'Intermediate' },
        { key: 'experienced', label: 'Experienced' }
      ],
      goals: [
        { key: 'fitness', label: 'Fitness & Conditioning' },
        { key: 'technique', label: 'Improve Technique' },
        { key: 'selfdefense', label: 'Self Defense' },
        { key: 'competition', label: 'Competition Preparation' },
        { key: 'strength', label: 'Strength & Power' },
        { key: 'flexibility', label: 'Flexibility & Mobility' },
        { key: 'endurance', label: 'Endurance & Cardio' },
        { key: 'coordination', label: 'Coordination & Balance' },
        { key: 'sparring', label: 'Sparring & Combat Application' },
        { key: 'rehabilitation', label: 'Rehabilitation & Therapy' },
        { key: 'weightloss', label: 'Weight Loss' },
        { key: 'stressrelief', label: 'Stress Relief & Relaxation' },
        { key: 'fun', label: 'Fun & Hobby' }
      ]
    }
  }

  const handleComplete = () => {
    if (!formData.name || !formData.experience_level || formData.training_goal.length === 0) return

    const userData: Partial<BetaUser> = {
      id: existingUser?.id || authUser?.id,
      name: formData.name,
      age: formData.age,
      experience_level: formData.experience_level as BetaUser['experience_level'],
      training_goal: formData.training_goal,
      email: existingUser?.email || authUser?.email,
      profile_completed: true
    }
    onComplete(userData)
  }

  return (
    <div className="max-w-md mx-auto">
      <Card className="p-4">
        {/* Language Toggle */}
        <div className="flex justify-end mb-3">
          <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => onLanguageChange('de')}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                language === 'de'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🇩🇪 DE
            </button>
            <button
              onClick={() => onLanguageChange('en')}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                language === 'en'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              🇺🇸 EN
            </button>
          </div>
        </div>

        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
            <User className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-1">
            {setupT[language].title}
          </h2>
          <p className="text-gray-600 dark:text-slate-200 mb-2 text-sm">
            {setupT[language].subtitle}
          </p>
          {authUser?.email && (
            <p className="text-xs text-purple-600">
              {authUser.email}
            </p>
          )}
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              {setupT[language].name}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={setupT[language].namePlaceholder}
              className="w-full px-3 py-2 border border-gray-300 dark:border-night-600 rounded-lg bg-white dark:bg-night-700 text-gray-900 dark:text-slate-100 placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              {setupT[language].age}: {formData.age} {setupT[language].years}
            </label>
            <input
              type="range"
              min="16"
              max="60"
              value={formData.age}
              onChange={(e) => setFormData(prev => ({ ...prev, age: parseInt(e.target.value) }))}
              className="w-full h-2 bg-gray-200 dark:bg-night-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              {setupT[language].experience}
            </label>
            <div className="space-y-1">
              {setupT[language].levels.map(level => (
                <button
                  key={level.key}
                  onClick={() => setFormData(prev => ({ ...prev, experience_level: level.key as 'anfaenger' | 'fortgeschritten' | 'erfahren' }))}
                  className={`w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                    formData.experience_level === level.key
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'bg-gray-100 dark:bg-night-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-night-600'
                  }`}
                >
                  {level.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              {setupT[language].goal}
            </label>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {setupT[language].goals.map(goal => {
                const isSelected = formData.training_goal.includes(goal.key)
                return (
                  <label
                    key={goal.key}
                    className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      isSelected
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                        : 'bg-gray-100 dark:bg-night-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-night-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => {
                        const isChecked = e.target.checked
                        setFormData(prev => ({
                          ...prev,
                          training_goal: isChecked
                            ? [...prev.training_goal, goal.key]
                            : prev.training_goal.filter(g => g !== goal.key)
                        }))
                      }}
                      className="mr-3 h-4 w-4 text-green-600 dark:text-green-400 border-gray-300 dark:border-night-500 bg-white dark:bg-night-700 rounded focus:ring-green-500 dark:focus:ring-green-400"
                    />
                    {goal.label}
                  </label>
                )
              })}
            </div>
          </div>

          <Button
            onClick={handleComplete}
            disabled={!formData.name || !formData.experience_level || formData.training_goal.length === 0}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white py-2"
          >
            {setupT[language].start}
          </Button>
        </div>
      </Card>
    </div>
  )
}
